import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import {
  supervisorAdd,
  supervisorGetById,
  supervisorOptionAllGet,
  supervisorUpdate
} from '../../../store/slices/tenantSlices/supervisorSlice';
import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';

const AddEditSupervisorModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [processOptions, setProcessOptions] = useState([]);
  const [supervisorRoleOptions, setSupervisorRoleOptions] = useState([]);

  const initialValues = {
    username: '',
    password: '',
    superVisorRoleId: '',
    status: 'true',
    is_auditor: 'yes',
    process_ids: []
  };

  const validationSchema = Yup.object({
    username: Yup.string().required('username is required'),
    password: Yup.string().required('password is required'),
    superVisorRoleId: Yup.string().required('supervisor role is required'),
    process_ids: Yup.array().min(1, 'process ID is required').required('process ID is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        const response = await dispatch(supervisorUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        const response = await dispatch(supervisorAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchSupervisorById = async () => {
    try {
      const response = await dispatch(supervisorGetById(editRecord?.id)).unwrap();
      const process_ids = response?.data?.supervisorProcess?.map((x) => x.process_id);
      setValues({
        ...response?.data?.supervisor,
        status: String(response?.data?.supervisor?.status),
        is_auditor: String(response?.data?.supervisor?.is_auditor || ''),
        superVisorRoleId: response?.data?.supervisor?.supervisor_role_id || '',
        process_ids: [...process_ids]
      });
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchSupervisorById();
    }
  }, [editRecord]);

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getSupervisorOption = async () => {
    try {
      const response = await dispatch(supervisorOptionAllGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setSupervisorRoleOptions(options);
      } else {
        setSupervisorRoleOptions([]);
      }
    } catch (error) {
      setSupervisorRoleOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getProcessOption();
      getSupervisorOption();
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Supervisor' : 'Add Supervisor'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="supervisor" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="username"
                label="Username"
                placeholder="Enter Username"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="password"
                label="Password"
                placeholder="Enter Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="superVisorRoleId"
                label="Supervisor Role"
                placeholder="Select Supervisor Role"
                value={values.superVisorRoleId}
                options={supervisorRoleOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="process_ids"
                label="Process"
                placeholder="Select Process"
                value={values.process_ids}
                options={processOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isMulti
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="is_auditor"
                label="Auditor"
                trueName="Yes"
                falseName="No"
                value={String(values.is_auditor === 'yes')}
                onChange={() =>
                  setFieldValue('is_auditor', values.is_auditor === 'yes' ? 'no' : 'yes')
                }
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="supervisor" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditSupervisorModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditSupervisorModal;
